import { Fragment, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, ContactRound, FileSignature, FileText, Handshake } from 'lucide-react';
import { PageHeader } from '@/components/patterns';
import { cn } from '@/lib/utils/cn';
import { useAuth } from '@/features/auth/hooks/use-auth';

export type PasoDeGestion = 'clientes' | 'oportunidades' | 'cotizaciones' | 'contratos';

/**
 * A dónde lleva cada paso de Gestión, según los módulos de la persona. Las
 * cotizaciones son las del Pricer Datec si la empresa lo tiene, y si no las
 * generales; la otra lista, cuando hay las dos, se elige debajo de los pasos.
 */
export function pasosDeGestion(
  puede: (k: string) => boolean,
  /**
   * Los módulos que trajo la sesión. Mientras no llegan, «puede» dice que sí a
   * todo para el administrador (para no esconderle nada), y llevarlo al Pricer
   * de una empresa que no lo tiene terminaba en «Este módulo no está incluido».
   * Por eso el Pricer se elige sólo si la lista lo confirma.
   */
  modulosDeLaSesion: string[] = []
) {
  const pricer = puede('pricer-datec') && modulosDeLaSesion.includes('menu:pricer-datec');
  const generales = puede('quotes');
  return [
    {
      id: 'clientes' as const,
      texto: 'Clientes',
      detalle: 'Con quién trabajás',
      href: '/customers',
      icono: ContactRound,
      visible: puede('customers'),
    },
    {
      id: 'oportunidades' as const,
      texto: 'Oportunidades',
      detalle: 'Lo que se está por vender',
      href: '/opportunities',
      icono: Handshake,
      visible: puede('opportunities'),
    },
    {
      id: 'cotizaciones' as const,
      texto: 'Cotizaciones',
      detalle: 'Lo que se cotizó',
      href: pricer ? '/pricer-datec/quotes' : '/quotes',
      icono: FileText,
      visible: pricer || generales,
    },
    {
      id: 'contratos' as const,
      texto: 'Contratos',
      detalle: 'Lo que se firmó',
      href: '/contracts',
      icono: FileSignature,
      visible: puede('contracts'),
    },
  ];
}

/**
 * Cotiza › Gestión (rediseño del 19-oct, artefacto «Gestión»): clientes,
 * oportunidades, cotizaciones y contratos en un solo lugar, como los pasos de
 * una venta. Cada paso es su pantalla de siempre, con su dirección: el
 * encabezado las une y marca dónde se está.
 *
 * Sólo se ofrecen los pasos que la persona puede abrir. Si la empresa tiene las
 * cotizaciones del Pricer Datec y las generales, «Cotizaciones» lleva a las del
 * Pricer y debajo se elige la otra lista: ninguna queda sin entrada.
 */
export function EncabezadoDeGestion({
  activa,
  titulo,
  subtitulo,
  acciones,
  dataTour,
}: {
  activa: PasoDeGestion;
  titulo?: ReactNode;
  subtitulo?: ReactNode;
  acciones?: ReactNode;
  /** El paso del tour que apunta al encabezado. */
  dataTour?: string;
}) {
  const { canSeeMenu, isModuleLocked, menuItems } = useAuth();
  const puede = (k: string) => canSeeMenu(k) && !isModuleLocked(k);
  const pasos = pasosDeGestion(puede, menuItems).filter((p) => p.visible || p.id === activa);
  const lasDosListas =
    puede('pricer-datec') && menuItems.includes('menu:pricer-datec') && puede('quotes');

  return (
    <div className="space-y-5">
      <div data-tour={dataTour}>
        <PageHeader
          modulo="cotiza"
          etiqueta="Cotiza · Gestión"
          icono={FileText}
          titulo={titulo ?? 'Gestión'}
          subtitulo={
            subtitulo ?? 'Clientes, oportunidades, cotizaciones y contratos en un solo lugar.'
          }
          acciones={acciones}
        />
      </div>

      <nav aria-label="Pasos de la venta" className="overflow-x-auto pb-1">
        <ol className="flex min-w-max items-stretch gap-2 md:min-w-0">
          {pasos.map((p, i) => {
            const Icono = p.icono;
            const esLaActiva = p.id === activa;
            return (
              <Fragment key={p.id}>
                {i > 0 && (
                  <li aria-hidden="true" className="flex items-center text-muted-foreground">
                    <ChevronRight className="h-4 w-4" />
                  </li>
                )}
                <li className="min-w-[180px] flex-1">
                  <Link
                    to={p.href}
                    aria-current={esLaActiva ? 'page' : undefined}
                    className={cn(
                      'flex h-full items-center gap-3 rounded-tarjeta border p-4 shadow-sm transition-[border-color,box-shadow] duration-snap ease-snap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      esLaActiva
                        ? 'border-transparent bg-degradado-prixia text-primary-foreground shadow-md dark:text-foreground'
                        : 'bg-card hover:shadow-md'
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-10 w-10 shrink-0 place-items-center rounded-xl',
                        esLaActiva ? 'bg-black/15' : 'bg-primary/10 text-primary'
                      )}
                    >
                      <Icono className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-semibold leading-tight">{p.texto}</span>
                      <span
                        className={cn(
                          'block truncate text-sm',
                          esLaActiva ? 'opacity-85' : 'text-muted-foreground'
                        )}
                      >
                        {p.detalle}
                      </span>
                    </span>
                  </Link>
                </li>
              </Fragment>
            );
          })}
        </ol>
      </nav>

      {activa === 'cotizaciones' && lasDosListas && <ListasDeCotizaciones />}
    </div>
  );
}

/** Con el Pricer Datec y las cotizaciones generales a la vez: elegir cuál lista ver. */
function ListasDeCotizaciones() {
  const enPricer = useLocation().pathname.includes('/pricer-datec/');
  const opciones = [
    { href: '/pricer-datec/quotes', texto: 'Del Pricer Datec', activa: enPricer },
    { href: '/quotes', texto: 'Generales', activa: !enPricer },
  ];
  return (
    <nav
      aria-label="Qué cotizaciones ver"
      className="inline-flex overflow-hidden rounded-md border bg-campo"
    >
      {opciones.map((o, i) => (
        <Link
          key={o.href}
          to={o.href}
          aria-current={o.activa ? 'page' : undefined}
          className={cn(
            'px-3 py-1.5 text-sm text-foreground/80 hover:bg-muted/60',
            i > 0 && 'border-l',
            o.activa && 'bg-primary/10 font-semibold text-primary hover:bg-primary/10'
          )}
        >
          {o.texto}
        </Link>
      ))}
    </nav>
  );
}
